import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { api, type Scheme, type StateRow } from "@/lib/api";

/**
 * Anveṣaṇa — unified inquiry palette.
 *
 * Single input. As the user types, we search *everything* in parallel
 * (schemes / states / ministries / personas), render each non-empty
 * bucket as a small section, and always keep a "Send to Ākāśavāṇī"
 * CTA at the bottom so the AI is never more than one Enter away.
 *
 * No tabs. No "pick a cable" block. No seed suggestions. z-index is
 * pushed above Leaflet's 1000 so the map legend can't bleed through.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onOpenScheme: (id: string) => void;
  onAskAi: (prompt: string) => void;
  onNavigate: (href: string) => void;
}

const TYPEWRITER = [
  "mudra loan for my cafe",
  "DPIIT recognition — how long does it take?",
  "seed fund for a healthtech startup in Bangalore",
  "PMEGP vs Stand-Up India — which for me?",
  "women entrepreneur schemes in Kerala",
  "T-Hub or NSRCEL — both? neither?",
  "tax holiday for year 3 onwards",
  "CGTMSE collateral-free limit this year",
];

const MINISTRIES: Array<{ slug: string; name: string; sigil: string; keywords: string[] }> = [
  { slug: "dpiit",    name: "DPIIT",                        sigil: "डी",  keywords: ["dpiit", "startup india", "recognition", "sisfs", "ffs"] },
  { slug: "msme",     name: "Ministry of MSME",             sigil: "सू",  keywords: ["msme", "udyam", "cgtmse", "pmegp", "mudra", "samadhaan"] },
  { slug: "dst",      name: "DST",                          sigil: "वि",  keywords: ["dst", "nidhi", "prayas", "eir", "seed support"] },
  { slug: "birac",    name: "BIRAC",                        sigil: "जै",  keywords: ["birac", "biotech", "big", "bipp", "sparsh"] },
  { slug: "aim",      name: "Atal Innovation Mission",      sigil: "अ",   keywords: ["aim", "atal", "tinkering", "incubation", "anic", "acic"] },
  { slug: "meity",    name: "MeitY",                        sigil: "डिजि", keywords: ["meity", "tide", "samridh", "ngis", "software", "silicon"] },
  { slug: "niti",     name: "NITI Aayog",                   sigil: "नीति", keywords: ["niti", "wep", "awe", "policy"] },
  { slug: "nabard",   name: "NABARD",                       sigil: "ग्रा", keywords: ["nabard", "rural", "agri", "dairy", "deds", "ridf"] },
  { slug: "sidbi",    name: "SIDBI",                        sigil: "सिडबी", keywords: ["sidbi", "maarg", "psig", "udyamimitra"] },
  { slug: "kvic",     name: "KVIC",                         sigil: "खादी", keywords: ["kvic", "khadi", "honey", "village"] },
  { slug: "textile",  name: "Ministry of Textiles",         sigil: "वस्त्र", keywords: ["textile", "yarn", "loom", "atufs", "pm mitra"] },
  { slug: "foodproc", name: "Food Processing",              sigil: "आहा", keywords: ["food", "processing", "pmfme", "kisan sampada"] },
];

const PERSONAS: Array<{ slug: string; label: string; tag: string; keywords: string[] }> = [
  { slug: "bengaluru-deeptech",  label: "The Bengaluru deep-techer",     tag: "karnataka · deeptech",   keywords: ["bengaluru", "bangalore", "deeptech", "karnataka", "ai", "deep tech"] },
  { slug: "surat-manufacturer",  label: "The Surat manufacturer",        tag: "gujarat · textile+gems", keywords: ["surat", "gujarat", "manufacturer", "textile", "gems"] },
  { slug: "kochi-woman-founder", label: "The Kochi woman founder",       tag: "kerala · women",         keywords: ["kochi", "kerala", "woman", "women", "ksum"] },
  { slug: "kanpur-leather",      label: "The Kanpur leather MSME",       tag: "up · leather",           keywords: ["kanpur", "uttar pradesh", "leather"] },
  { slug: "chennai-saas",        label: "The Chennai SaaS bootstrapper", tag: "tamil nadu · saas",      keywords: ["chennai", "tamil nadu", "saas", "bootstrap", "startuptn"] },
  { slug: "guwahati-agritech",   label: "The Guwahati agritech",         tag: "assam · agritech",       keywords: ["guwahati", "assam", "agritech", "north east"] },
  { slug: "kolkata-firstgen",    label: "The Kolkata first-gen",         tag: "west bengal · individual", keywords: ["kolkata", "west bengal", "first gen", "individual"] },
  { slug: "indore-food",         label: "The Indore food processor",     tag: "mp · food",              keywords: ["indore", "madhya pradesh", "food", "processor"] },
  { slug: "imphal-rural",        label: "The Imphal rural innovator",    tag: "manipur · rural",        keywords: ["imphal", "manipur", "rural", "grassroots", "nif"] },
];

export function CommandPalette({ open, onClose, onOpenScheme, onAskAi, onNavigate }: Props) {
  const [q, setQ]             = useState("");
  const [schemes, setSchemes] = useState<Scheme[]>([]);
  const [states, setStates]   = useState<StateRow[]>([]);
  const [twIdx, setTwIdx]     = useState(0);
  const [typed, setTyped]     = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Typewriter placeholder — cycles a hint character-by-character.
  useEffect(() => {
    if (q || !open) return;
    const target = TYPEWRITER[twIdx];
    let i = 0;
    setTyped("");
    const id = window.setInterval(() => {
      i++;
      if (i > target.length) {
        window.clearInterval(id);
        window.setTimeout(() => setTwIdx((n) => (n + 1) % TYPEWRITER.length), 2400);
        return;
      }
      setTyped(target.slice(0, i));
    }, 40);
    return () => window.clearInterval(id);
  }, [q, twIdx, open]);

  useEffect(() => {
    if (!open) return;
    // Lock body scroll so the page doesn't drift under the palette.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset once when closed so the next open starts fresh.
  useEffect(() => {
    if (!open) { setQ(""); setSchemes([]); setStates([]); }
  }, [open]);

  // Parallel fetch schemes + states on every keystroke (debounced).
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setSchemes([]); setStates([]); return; }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const [schResp, stResp] = await Promise.all([
          api.search({ q, limit: 6 }).catch(() => ({ items: [], total: 0 })),
          api.states().catch(() => ({ items: [] })),
        ]);
        if (cancelled) return;
        setSchemes(schResp.items);
        setStates(stResp.items.filter((s) => s.state.toLowerCase().includes(q.toLowerCase())).slice(0, 4));
      } catch { /* swallow */ }
    }, 140);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [open, q]);

  const filteredMinistries = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return MINISTRIES.filter((m) =>
      m.name.toLowerCase().includes(t) ||
      m.slug.includes(t) ||
      m.keywords.some((k) => k.includes(t) || t.includes(k)),
    ).slice(0, 4);
  }, [q]);

  const filteredPersonas = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return PERSONAS.filter((p) =>
      p.label.toLowerCase().includes(t) ||
      p.tag.includes(t) ||
      p.keywords.some((k) => k.includes(t) || t.includes(k)),
    ).slice(0, 4);
  }, [q]);

  const hasQuery = q.trim().length > 0;
  const hasResults = hasQuery && (
    schemes.length > 0 || states.length > 0 ||
    filteredMinistries.length > 0 || filteredPersonas.length > 0
  );

  const submitToOracle = () => {
    const text = q.trim();
    if (!text) return;
    onAskAi(text);
    onClose();
  };

  const onEnter = () => {
    if (!hasQuery) return;
    // Enter always goes to Ākāśavāṇī by default — the AI is the escape
    // hatch when nothing exact matches.
    submitToOracle();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        // z-[2000] pushes us above Leaflet's controls (z-index 1000)
        // so the map legend can't bleed through.
        style={{ zIndex: 2000 }}
        className="fixed inset-0 flex items-start justify-center p-4 md:p-8 pt-20 md:pt-24"
      >
        {/* Scrim — clicking it (or any non-card area) closes the palette. */}
        <button
          type="button"
          aria-label="Close Anveṣaṇa"
          onClick={onClose}
          className="absolute inset-0 bg-plum-950/90 backdrop-blur-md cursor-default"
        />

        <motion.div
          initial={{ y: -18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -18, opacity: 0 }}
          transition={{ type: "spring", stiffness: 180, damping: 22 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-3xl rounded-[1.25rem] border border-amber-400/25 bg-plum-900/98
                     shadow-[0_40px_120px_-30px_rgba(0,0,0,0.95)] overflow-hidden flex flex-col
                     max-h-[80vh]"
        >
          {/* Ribbon header */}
          <div className="flex items-center gap-3 px-5 py-2.5 border-b border-amber-400/15 shrink-0">
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300/90">
              · anveṣaṇa ·
            </span>
            <span className="flex-1" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream-100/40">
              ↵ ask ākāśavāṇī · esc close
            </span>
          </div>

          {/* Input */}
          <div className="relative border-b border-amber-400/10 shrink-0">
            <span className="absolute left-6 top-1/2 -translate-y-1/2 font-serif italic text-amber-300/75 text-base leading-none">¶</span>
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onEnter()}
              placeholder={typed ? `try: ${typed}` : "type anything — a scheme, a state, a ministry, a question…"}
              className="w-full bg-transparent border-0 outline-none pl-12 pr-6 py-5 font-display text-[22px] md:text-[26px]
                         text-cream-50 placeholder:text-cream-100/35"
              aria-label="Type your inquiry"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>

          {/* Body — scrolls if results get long */}
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {!hasQuery && <EmptyHint />}

            {hasQuery && !hasResults && (
              <div className="py-8 text-center">
                <p className="font-display text-lg text-cream-50/90">No exact matches filed.</p>
                <p className="mt-1 text-[13px] text-cream-100/55">
                  Press <span className="font-mono text-amber-300">↵</span> to ask Ākāśavāṇī — the seer will find a way.
                </p>
              </div>
            )}

            {hasResults && (
              <div className="space-y-5">
                {schemes.length > 0 && (
                  <Section heading="schemes">
                    <ul className="space-y-1.5">
                      {schemes.map((s, i) => (
                        <li key={s.id}>
                          <button
                            onClick={() => { onOpenScheme(s.id); onClose(); }}
                            className="group w-full flex items-start gap-3 rounded-lg border border-white/8 bg-plum-950/35
                                       hover:border-amber-400/35 hover:bg-plum-950/65 transition p-2.5 text-left"
                          >
                            <span className="font-serif italic text-amber-300/70 text-sm w-6 text-center shrink-0">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block font-display text-[13.5px] font-semibold text-cream-50 group-hover:text-amber-200 truncate">
                                {s.name}
                              </span>
                              <span className="block text-[10.5px] font-mono uppercase tracking-[0.2em] text-cream-100/45 mt-0.5 truncate">
                                {[s.level, s.state, s.source].filter(Boolean).join(" · ")}
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                {states.length > 0 && (
                  <Section heading="states">
                    <div className="grid md:grid-cols-2 gap-1.5">
                      {states.map((s) => (
                        <button
                          key={s.state}
                          onClick={() => { onNavigate(`/state/${slugify(s.state)}`); onClose(); }}
                          className="group flex items-center justify-between gap-2 rounded-lg border border-white/8 bg-plum-950/35
                                     hover:border-amber-400/35 hover:bg-plum-950/65 transition p-2.5 text-left"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-1 h-4 rounded-sm ${
                              s.total >= 50 ? "bg-coral-500" : s.total >= 20 ? "bg-amber-400" : "bg-aqua-400"
                            }`} />
                            <span className="font-display text-[13px] text-cream-50 group-hover:text-amber-200 truncate">
                              {s.state}
                            </span>
                          </div>
                          <span className="font-mono text-[10px] text-cream-100/45">
                            {s.total}
                          </span>
                        </button>
                      ))}
                    </div>
                  </Section>
                )}

                {filteredMinistries.length > 0 && (
                  <Section heading="ministries">
                    <div className="grid md:grid-cols-2 gap-1.5">
                      {filteredMinistries.map((m) => (
                        <button
                          key={m.slug}
                          onClick={() => { onNavigate(`/ministry/${m.slug}`); onClose(); }}
                          className="group flex items-center gap-2.5 rounded-lg border border-white/8 bg-plum-950/35
                                     hover:border-amber-400/35 hover:bg-plum-950/65 transition p-2.5 text-left"
                        >
                          <span className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center font-serif italic text-amber-300 text-xs
                                           border border-amber-400/35 bg-plum-950/60">
                            {m.sigil}
                          </span>
                          <span className="font-display text-[13px] font-semibold text-cream-50 group-hover:text-amber-200 truncate">
                            {m.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </Section>
                )}

                {filteredPersonas.length > 0 && (
                  <Section heading="personas">
                    <div className="grid md:grid-cols-2 gap-1.5">
                      {filteredPersonas.map((p) => (
                        <button
                          key={p.slug}
                          onClick={() => { onNavigate(`/persona/${p.slug}`); onClose(); }}
                          className="group flex items-start gap-2 rounded-lg border border-white/8 bg-plum-950/35
                                     hover:border-amber-400/35 hover:bg-plum-950/65 transition p-2.5 text-left"
                        >
                          <span className="font-serif italic text-amber-300/70 text-base leading-none pt-0.5">✦</span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-display text-[13px] font-semibold text-cream-50 group-hover:text-amber-200 truncate">
                              {p.label}
                            </span>
                            <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-cream-100/45">
                              {p.tag}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            )}
          </div>

          {/* Bottom Ākāśavāṇī rail — always the default next step once the
              user has typed something. */}
          {hasQuery && (
            <button
              onClick={submitToOracle}
              className="group shrink-0 flex items-center gap-3 border-t border-amber-400/25
                         bg-gradient-to-r from-amber-400/[0.12] to-coral-500/[0.10]
                         hover:from-amber-400/[0.20] hover:to-coral-500/[0.18]
                         px-5 py-3.5 text-left transition"
            >
              <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-amber-400/45 bg-amber-400/15">
                <Sparkles className="w-4 h-4 text-amber-300 group-hover:rotate-12 transition-transform" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-mono text-[9px] uppercase tracking-[0.35em] text-amber-300">cable ākāśavāṇī</span>
                <span className="block font-display text-[15px] text-cream-50 group-hover:text-amber-200 truncate mt-0.5">
                  “{q.trim()}”
                </span>
              </span>
              <ArrowRight className="w-4 h-4 text-amber-300 shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </button>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* --------- Small helpers --------- */

function EmptyHint() {
  return (
    <div className="py-8 text-center px-4">
      <p className="font-display text-lg text-cream-50/90">Inquire of the atlas.</p>
      <p className="mt-1.5 text-[13px] text-cream-100/55">
        Schemes, states, ministries and founder archetypes all live here. Type a word — matches surface below.
      </p>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/70">
        ↵ routes the unanswered to ākāśavāṇī
      </p>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 px-1">
        <span className="h-px flex-1 bg-amber-400/15" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/80">{heading}</span>
        <span className="h-px flex-1 bg-amber-400/15" />
      </div>
      {children}
    </div>
  );
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
